import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { LivePageErrorBoundary } from './components/LivePageErrorBoundary';
import { VoiceDemo } from './components/VoiceDemo';
import { useSessionSafeSwUpdate } from './lib/sw-update';

const Admin = lazy(() => import('./pages/Admin'));
const StaffFloorView = lazy(() => import('./pages/StaffFloorView'));
// R28 — dev-only visual harness for Wave-2 screens (both-theme QA + Playwright proof). Lazy, so it
// never enters the main bundle; reachable only via the explicit /__uikit URL (no nav links to it).
const UiKit = lazy(() => import('./pages/UiKit'));

/**
 * Booth kiosk routes. No public access gate (RESKIN §3) — the kiosk URL is private and
 * per-event setup is protected by the admin passcode, so "/" renders the concierge directly.
 *
 * Round 8 additions:
 *  - `useSessionSafeSwUpdate()` initialises the session-aware SW update guard (Bug 1 fix):
 *    a new SW waits until the session is idle before activating; between-sessions banner
 *    surfaces via `needRefresh` so staff can acknowledge without mid-session disruption.
 *  - `<LivePageErrorBoundary>` wraps the live VoiceDemo so render errors surface a
 *    "Start over" affordance instead of a blank/crashed screen (Bug 1 fix — error boundary).
 */
export function App() {
  const { needRefresh, applyUpdate } = useSessionSafeSwUpdate();

  return (
    <>
      {needRefresh && <UpdateReadyBanner onApply={applyUpdate} />}
      <Routes>
        <Route
          path="/"
          element={
            <LivePageErrorBoundary>
              <VoiceDemo />
            </LivePageErrorBoundary>
          }
        />
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<div className="p-8">Loading admin…</div>}>
              <Admin />
            </Suspense>
          }
        />
        <Route
          path="/staff"
          element={
            <Suspense fallback={<div className="p-8">Loading…</div>}>
              <StaffFloorView />
            </Suspense>
          }
        />
        {/* R29 X5 — the dev-only visual harness is prod-EXCLUDED: the route (and its lazy chunk) are
            registered only when the build-time `__ENABLE_UIKIT__` flag is set (dev server + the
            `UIKIT=1` screenshot build; false in the real production deploy). See vite.config.ts. */}
        {__ENABLE_UIKIT__ && (
          <Route
            path="/__uikit"
            element={
              <Suspense fallback={<div className="p-8">Loading…</div>}>
                <UiKit />
              </Suspense>
            }
          />
        )}
        <Route path="/health" element={<HealthPing />} />
      </Routes>
    </>
  );
}

/**
 * Non-blocking between-sessions update banner (Bug 1 fix — round-8-voice).
 * Only rendered when a new SW is waiting. Shown at top of the page — does not
 * block or overlay the concierge. Staff can apply it between visitors.
 */
function UpdateReadyBanner({ onApply }: { onApply: () => void }) {
  return (
    <output
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'var(--c-brass-soft, rgba(143,110,38,0.15))',
        borderBottom: '1px solid var(--c-brass, #8f6e26)',
        padding: '0.5rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        fontFamily: 'var(--font-body, system-ui)',
        fontSize: '0.8125rem',
        color: 'var(--c-ink, #f0ece6)',
      }}
    >
      <span>Update ready — will apply between sessions.</span>
      <button
        type="button"
        onClick={onApply}
        style={{
          padding: '0.25rem 0.875rem',
          borderRadius: '9999px',
          background: 'var(--c-walnut, #5c3d2e)',
          color: 'var(--c-on-accent, #f0ece6)',
          border: 'none',
          fontSize: '0.75rem',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Apply now
      </button>
    </output>
  );
}

function HealthPing() {
  return (
    <pre style={{ padding: '1rem', fontFamily: 'monospace' }}>
      {JSON.stringify(
        {
          ok: true,
          service: 'godgift-voice-pwa',
          version: import.meta.env.MODE,
        },
        null,
        2
      )}
    </pre>
  );
}
